/**
 * EditorState — internal state container for the editor.
 *
 * Replaces the three Vuex modules (PanelsApp, PanelsEditor, PanelsScene).
 * A plain object with setter methods that emit change events via EventBus.
 * All editor classes receive this + the EventBus via constructor injection.
 */

import { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import {
  ControlModeTypes,
  TransformControlModeTypes,
  TransformControlsSpaceTypes,
  type ControlModeType,
  type TransformControlModeType,
  type TransformControlsSpaceType,
} from '@shared/constants';

/** Shape of a scene node in internal state. */
export interface SceneState {
  sceneID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

/** Shape of an element (panel) in internal state. */
export interface ElementState {
  sceneID: string;
  assetID: string;
  parentID: string;
  position: number[];
  rotation: number[];
  scale: number[];
}

export class EditorState {
  // --- Loading flags ---
  bootstrapComplete = false;

  // --- Navigation and control modes ---
  navigationLocked = false;
  controlMode: ControlModeType = ControlModeTypes.DEFAULT;
  transformControlsMode: TransformControlModeType = TransformControlModeTypes.TRANSLATE;
  transformControlsSpace: TransformControlsSpaceType = TransformControlsSpaceTypes.WORLD;
  transformControlsSnapping = false;

  // --- Selection ---
  selectedElements: string[] = [];
  hoveredElement: string | null = null;

  // --- Scene data ---
  scenes: SceneState[] = [];
  elements: ElementState[] = [];

  // --- App state ---
  muted = true;

  private events: EventBus<EditorEvents>;

  constructor(events: EventBus<EditorEvents>) {
    this.events = events;
  }

  // --- Setters with event emission ---

  setSelectedElements(elementIds: string[]): void {
    const previousIds = this.selectedElements;
    this.selectedElements = elementIds;

    // Emit deselect if we had a selection before
    if (previousIds.length > 0 && (elementIds.length === 0 || previousIds[0] !== elementIds[0])) {
      this.events.emit('element:deselected');
    }

    // Emit select if we now have a selection
    if (elementIds.length > 0) {
      this.events.emit('element:selected', { elementId: elementIds[0] });
    }
  }

  setControlMode(mode: ControlModeType): void {
    if (this.controlMode === mode) return;
    this.controlMode = mode;
    this.events.emit('controlMode:changed', { mode });
  }

  setTransformControlsMode(mode: TransformControlModeType): void {
    if (this.transformControlsMode === mode) return;
    this.transformControlsMode = mode;
    this.events.emit('transformMode:changed', { mode });
  }

  setTransformControlsSpace(space: TransformControlsSpaceType): void {
    if (this.transformControlsSpace === space) return;
    this.transformControlsSpace = space;
    this.events.emit('transformSpace:changed', { space });
  }

  setTransformControlsSnapping(enabled: boolean): void {
    if (this.transformControlsSnapping === enabled) return;
    this.transformControlsSnapping = enabled;
    this.events.emit('snapping:changed', { enabled });
  }

  setNavigationLocked(locked: boolean): void {
    if (this.navigationLocked === locked) return;
    this.navigationLocked = locked;
    this.events.emit('navigation:locked', { locked });
  }

  setHoveredElement(elementId: string | null): void {
    this.hoveredElement = elementId;
    if (elementId) {
      this.events.emit('element:hovered', { elementId });
    } else {
      this.events.emit('element:hovered', null);
    }
  }

  setScenes(scenes: SceneState[]): void {
    this.scenes = scenes;
  }

  setElements(elements: ElementState[]): void {
    this.elements = elements;
  }

  setBootstrapComplete(complete: boolean): void {
    this.bootstrapComplete = complete;
    if (complete) {
      this.events.emit('bootstrap:complete');
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /**
   * Reset all state to defaults.
   */
  reset(): void {
    this.bootstrapComplete = false;
    this.navigationLocked = false;
    this.controlMode = ControlModeTypes.DEFAULT;
    this.transformControlsMode = TransformControlModeTypes.TRANSLATE;
    this.transformControlsSpace = TransformControlsSpaceTypes.WORLD;
    this.transformControlsSnapping = false;
    this.selectedElements = [];
    this.hoveredElement = null;
    this.scenes = [];
    this.elements = [];
    this.muted = true;
  }
}
